import { lazy, Suspense, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentGroups, TranslationNamespaces } from '~/lib/constants';
import { Tagged } from '~/apps/smartsend/components/Tagged';
import { ContentContext } from '@parcel2go/npm-p2g-app';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { NavigationContext } from '~/components/nav/nav-context';
import useOnLoad from '~/routes/use-on-load';
import { P2GBox } from '@parcel2go/core-ui';
import useGoto from '~/hooks/use-goto';
import { useNavExperience } from '~/components/nav/nav-experience';
import { SettingsCache } from '~/lib/consts/settings-cache';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

// Stored Cards management page. Dark-released behind the 'StoredCards' tenant feature flag; card
// data flows through the stored-cards adapter, which is on the mock until `USE_REAL_CARDS_API` is
// flipped. The payments surface itself has shipped (Parcel2Go/quoting-api#1495) - what is dark is
// this repo's switch and the tenant's `Payment.StoredCards.Enabled`, not the API.
const StoredCards = lazy(() => import('~/apps/stored-cards/stored-cards'));

export default function Component() {
  const { goTo } = useGoto();
  const experience = useNavExperience();
  const { track } = useSmartSendAnalytics();
  const { dispatch } = useContext(ContentContext);
  const { dispatch: navigationDispatch } = useContext(NavigationContext);
  const { t } = useTranslation(TranslationNamespaces.STORED_CARDS);

  useOnLoad(() => {
    // Dark release: the route is always registered, so direct navigation must
    // bounce when the tenant flag is off (same pattern as routes/rewards).
    //
    // The destination depends on which experience is mounting this, because this wrapper is
    // registered in BOTH route groups. `RoutingConstants.MyAccount` is the empty string - the My
    // Account root, and correct for the business experience - but it is not in
    // `CONSUMER_ROUTE_SEGMENTS`, so `useGoto` does not prepend the `account/` base and a consumer
    // would be dropped at `/:prefix/`, outside the area they were in. `MyDetails` is the first tab
    // of the same Account layout this page sits in, so a consumer lands where the tab would have
    // been rather than out of the section altogether.
    if (!SettingsCache.settings.Features.includes('StoredCards')) {
      goTo(experience === 'consumer' ? RoutingConstants.MyDetails : RoutingConstants.MyAccount);
      return;
    }

    const title = t('STORED_CARDS_PAGE_TITLE', { defaultValue: 'Stored Cards' });
    document.title = title;
    dispatch({ type: 'SET_CONTENT_GROUP', contentGroup: ContentGroups.STORED_CARDS });
    navigationDispatch({
      type: 'INITIALISE_NAVIGATION',
      payload: { title: title, currentPageRoute: RoutingConstants.StoredCards }
    });
    track({
      type: 'page_view',
      payload: {
        ...GenerateBaseAnalyticsPayload([]),
        page: RoutingConstants.StoredCards
      }
    });
  });

  return (
    <Tagged tag="screen-stored-cards">
      <Suspense>
        <P2GBox>
          <StoredCards />
        </P2GBox>
      </Suspense>
    </Tagged>
  );
}

Component.displayName = 'Stored Cards';
